import { Logger } from '@lambda/commons/logging/logger';
import { datadogLogs } from '@datadog/browser-logs';
import settings from '@/settings';

const logger = new Logger('cart-out-of-stock-logger');

export interface OutOfStockCartLogData {
  // Product/Offer Information
  sku: string;
  psku: string;
  title: string;
  vendor: string;
  price: number | string;
  condition?: string;
  color?: string;
  brand?: string;
  storage?: string;
  category?: string;
  
  // Cart Information
  quantity: number;
  cartId?: string;
  cartSize?: number;
  
  // User/Context Information
  userAgent?: string;
  url: string;
  referrer?: string;
  
  // Stock Information
  availableStock: number;
  requestedQuantity: number;
  
  // Additional Context
  itemPosition?: number;
  accessoryModalSource?: boolean;
  timestamp: string;
}

export const logOutOfStockCartAddition = (data: OutOfStockCartLogData) => {
  const {
    sku,
    psku,
    title,
    vendor,
    price,
    condition,
    color,
    brand,
    storage,
    category,
    quantity,
    cartId,
    cartSize,
    userAgent,
    url,
    referrer,
    availableStock,
    requestedQuantity,
    itemPosition,
    accessoryModalSource,
    timestamp,
  } = data;

  // Create a comprehensive log message
  const logMessage = `🚨 OUT OF STOCK CART ADDITION DETECTED - User attempted to add out-of-stock item to cart`;
  
  // Create detailed log object for structured logging
  const logData = {
    event: 'cart_out_of_stock_addition',
    severity: 'ERROR',
    store: settings.store,
    timestamp,
    
    // Product Details
    product: {
      sku,
      psku,
      title,
      vendor,
      price,
      condition,
      color,
      brand,
      storage,
      category,
    },
    
    // Stock Information
    stock: {
      available: availableStock,
      requested: requestedQuantity,
      deficit: requestedQuantity - availableStock,
    },
    
    // Cart Context
    cart: {
      cartId,
      cartSize,
      quantity,
      itemPosition,
      accessoryModalSource,
    },
    
    // User Context
    user: {
      userAgent,
      url,
      referrer,
    },
    
    // Additional Metadata
    metadata: {
      environment: process.env.NEXT_PUBLIC_STAGE || process.env.STAGE || 'unknown',
      service: 'storefront',
      component: 'cart',
    },
  };

  // Log to console with clear formatting
  console.error('🚨 OUT OF STOCK CART ADDITION DETECTED 🚨');
  console.error('==========================================');
  console.error(`Product: ${title}`);
  console.error(`SKU: ${sku} | PSKU: ${psku}`);
  console.error(`Vendor: ${vendor}`);
  console.error(`Price: ${price}`);
  console.error(`Available Stock: ${availableStock}`);
  console.error(`Requested Quantity: ${requestedQuantity}`);
  console.error(`URL: ${url}`);
  console.error(`Timestamp: ${timestamp}`);
  console.error('==========================================');

  // Log to structured logger (goes to Datadog)
  logger.error(logData, logMessage);

  // Send to Datadog Browser Logs for real-time monitoring
  try {
    datadogLogs.logger.error(logMessage, {
      ...logData,
      // Add Datadog-specific tags for alerting
      dd: {
        tags: [
          `store:${settings.store}`,
          `sku:${sku}`,
          `vendor:${vendor}`,
          `event:cart_out_of_stock_addition`,
          `severity:error`,
        ],
      },
    });
  } catch (error) {
    // Fallback if Datadog logs fail
    console.error('Failed to send to Datadog logs:', error);
  }
};

export const createOutOfStockLogData = (
  productData: {
    sku: string;
    psku: string;
    title: string;
    vendor: string;
    price: number | string;
    condition?: string;
    color?: string;
    brand?: string;
    storage?: string;
    category?: string;
  },
  cartData: {
    quantity: number;
    cartId?: string;
    cartSize?: number;
    itemPosition?: number;
    accessoryModalSource?: boolean;
  },
  stockData: {
    availableStock: number;
    requestedQuantity: number;
  }
): OutOfStockCartLogData => {
  return {
    ...productData,
    ...cartData,
    ...stockData,
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
    url: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof window !== 'undefined' ? document.referrer : undefined,
    timestamp: new Date().toISOString(),
  };
}; 